import { memo, useCallback, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import {
    getMatchedTransaction,
    getTransactionMatchFilters,
} from "@components/documents/lib";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import {
    TransactionPreview,
    TransactionPreviewData,
} from "@components/transactions/TransactionPreview/TransactionPreview";
import styles from "./MatchedTransactionCell.module.scss";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const MatchedTransactionCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, FinancialDocumentListItem, unknown>) => {
        const document = row.original;
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const matchedFinancialTransaction = getMatchedTransaction(document);
        const openTransactions = useGlobalTransactionViewDrawer();

        const handleClick = useCallback(() => {
            if (matchedFinancialTransaction) {
                openTransactions(
                    getTransactionMatchFilters(document.transactionMatches),
                );
            }
        }, [matchedFinancialTransaction, openTransactions, document]);

        const previewData: TransactionPreviewData | null = useMemo(() => {
            if (!matchedFinancialTransaction) {
                return null;
            }
            return {
                id: matchedFinancialTransaction.id,
                date: matchedFinancialTransaction.date,
                bankDescription: matchedFinancialTransaction.bankDescription,
                displayDescription:
                    matchedFinancialTransaction.displayDescription,
                categoryId: matchedFinancialTransaction.categoryId,
                entityId: matchedFinancialTransaction.entityId,
                financialAccountId:
                    matchedFinancialTransaction.financialAccountId,
                amount: matchedFinancialTransaction.amount,
            };
        }, [matchedFinancialTransaction]);

        return (
            <ViewTableCell captureClicks className={styles.cell}>
                {previewData && (
                    <EditableCellContent
                        className={classNames({
                            [styles.pairedTransfer]:
                                !!matchedFinancialTransaction,
                        })}
                        onClick={isTableReadonly ? undefined : handleClick}
                    >
                        <TransactionPreview transaction={previewData} />
                    </EditableCellContent>
                )}
            </ViewTableCell>
        );
    },
);
